import { useCallback, useEffect, useState } from 'react';
import {
  Divider,
  DialogContent,
  DialogActions,
  Dialog,
  Button,
  MenuItem,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, UseFormReturn, useWatch } from 'react-hook-form';
import { LoadingButton } from '@mui/lab';
import { get } from 'lodash';
import { yupResolver } from '@hookform/resolvers/yup';

import { RoleId, Strings } from '@app/constants';
import { IExistingLeaveSchema, ILeave, ILeaveSchema, IPeriods, IUser } from '../types';
import { Header } from '@app/sections/header';
import {
  FormProvider,
  Iconify,
  RHFDatePicker,
  RHFFilePickerField,
  RHFSelect,
  RHFTextField,
  Scrollbar,
} from '@app/components';
import { getLeaveManagement, selectLeaveManagementState } from '@app/modules/leave-setting';
import { selectAddExistingLeaveState, selectGetUsersState } from '../selectors';
import { useDialog, useRequestState } from '@app/hooks';
import { SelectPeriod } from './select-period';
import { addExistingLeaveSchema } from '../validator';
import { addExistingLeave, getUsers } from '../slice';
import { isWeekend } from '@app/utils';

interface Props {
  open: boolean;
  onClose: VoidFunction;
  row?: ILeave;
}
const AddExistingLeave = ({ open, onClose: handleOnCloseLeavePopup, row }: Props) => {
  const methods = useForm<IExistingLeaveSchema>({
    resolver: yupResolver(addExistingLeaveSchema) as any,
    defaultValues: {
      leaveType: get(row, 'leaveType', ''),
      from: get(row, 'from', ''),
      to: get(row, 'to', ''),
      reason: get(row, 'reason', ''),
      attachment: '',
      periods: row?.periods,
      description: '',
      name: undefined,
      approvedBy: undefined,
    },
  });
  const leaveState = useSelector(selectLeaveManagementState);
  const theme = useTheme();
  const dispatch = useDispatch();
  const { control } = methods;
  const file = useWatch({ control, name: 'attachment' });

  useEffect(() => {
    dispatch(getUsers());
    dispatch(getLeaveManagement());
  }, [dispatch]);

  const { data: usersData } = useRequestState({
    stateSelector: selectGetUsersState,
  });

  const [fileError, setFileError] = useState<string | null>(null);
  const [currentApplyingLeaves, setCurrentApplyingLeaves] = useState<number>(0);
  const [periods, setPeriods] = useState<IPeriods[]>([]);

  const [selectedDates, setSelectedDates] = useState<{ from: Dayjs | null; to: Dayjs | null }>({
    from: dayjs(row?.from, 'DD/MM/YYYY'),
    to: dayjs(row?.to, 'DD/MM/YYYY'),
  });
  const selectPeriodDialog = useDialog();

  const handleSelectedFromDate = (date: Dayjs) => {
    setSelectedDates((prevState) => ({
      ...prevState,
      from: date,
    }));
  };

  const handleSelectedToDate = (date: Dayjs) => {
    setSelectedDates((prevState) => ({
      ...prevState,
      to: date,
    }));
  };

  const validateFile = (file: any) => {
    if (!file) return true;

    const validTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];
    if (!validTypes.includes(file.type)) {
      setFileError('Only PDF, PNG, and JPG files are allowed');
      return false;
    }
    setFileError(null);
    return true;
  };

  const handleTotalLeaves = (currentApplyingLeaves: number) => {
    setCurrentApplyingLeaves(currentApplyingLeaves);
  };
  const handleSelectedPeriod = (updatedLeaves: IPeriods[]) => {
    setPeriods(updatedLeaves);
  };

  const superAdmin: IUser = usersData?.data?.find(
    (user: IUser) => user.roleId === RoleId.superAdmin
  );

  const managersList: IUser[] =
    usersData?.data?.filter((user: IUser) => user.roleId === RoleId.manager) ?? [];

  const onSubmitSend = (form: IExistingLeaveSchema) => {
    if (!validateFile(file)) return;
    dispatch(
      addExistingLeave({
        ...form,
        periods,
        attachment: file,
      })
    );
  };

  const selectedLeaveTaker = methods.watch('name');
  const leaveUserDetails: IUser = usersData?.data?.find(
    (user: IUser) => user.id === Number(selectedLeaveTaker)
  );
  const handleAddExistingLeaveSuccess = useCallback(() => {
    handleOnCloseLeavePopup();
  }, []);

  const { loading: addExistingLeaveLoading } = useRequestState({
    stateSelector: selectAddExistingLeaveState,
    onSuccess: handleAddExistingLeaveSuccess,
    successMessageShown: true,
    errorShown: true,
  });

  return (
    <>
      <FormProvider methods={methods}>
        <Dialog open={open} fullWidth>
          <Header
            variant="dialog"
            title={Strings.leave.addExistingLeave}
            onClose={handleOnCloseLeavePopup}
          />
          <Divider />
          <Scrollbar>
            <DialogContent sx={{ py: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Stack spacing={1} my={2}>
                <RHFSelect required name={'name'} label={Strings.leave.name}>
                  {usersData?.data?.map((user: IUser) => {
                    return (
                      user.roleId !== RoleId.superAdmin &&
                      user.roleId !== RoleId.candidate && (
                        <MenuItem value={user.id}>
                          <Typography>{user.firstName + ' ' + user.lastName}</Typography>
                        </MenuItem>
                      )
                    );
                  })}
                </RHFSelect>
                <RHFTextField
                  name="leaveType"
                  label={Strings.field.type}
                  required
                  variant="outlined"
                  select
                >
                  {leaveState.data?.map(
                    (option) =>
                      option.status && (
                        <MenuItem key={option.id} value={option.leaveType}>
                          {option.leaveType}
                        </MenuItem>
                      )
                  )}
                </RHFTextField>
                <RHFDatePicker
                  maxDate={selectedDates.to}
                  name="from"
                  label={Strings.field.from}
                  required
                  shouldDisableDate={isWeekend}
                  onChange={(date) => {
                    handleSelectedFromDate(date);
                  }}
                  defaultValue={row ? dayjs(row.from, 'DD/MM/YYYY') : null}
                />
                <RHFDatePicker
                  minDate={selectedDates.from}
                  name="to"
                  label={Strings.field.to}
                  required
                  shouldDisableDate={isWeekend}
                  onChange={(date) => {
                    handleSelectedToDate(date);
                    selectPeriodDialog.show();
                  }}
                  defaultValue={row ? dayjs(row.to, 'DD/MM/YYYY') : null}
                />
                {selectedDates.from && selectedDates.to && (
                  <Stack direction={'row'} pt={2} pb={2} justifyContent={'space-between'}>
                    <Stack direction={'row'} gap={1}>
                      <Stack color={theme.palette.grey[600]}>
                        <Iconify icon="mdi:progress-clock" />
                      </Stack>
                      <Stack direction={'row'} gap={0.5}>
                        <Typography color={theme.palette.grey[500]} fontSize={14}>
                          {Strings.leave.youAreRequesting}
                        </Typography>
                        <Typography fontWeight={700} color={theme.palette.grey[600]} fontSize={15}>
                          {currentApplyingLeaves.toString()} {Strings.field.days}
                        </Typography>
                      </Stack>
                    </Stack>
                    <Stack>
                      <Button
                        variant="outlined"
                        color="inherit"
                        onClick={selectPeriodDialog.show}
                        size="small"
                      >
                        {Strings.button.changePeriod}
                      </Button>
                    </Stack>
                  </Stack>
                )}
                <RHFSelect required name="approvedBy" label={Strings.leave.approvedBy}>
                  {(leaveUserDetails?.roleId === RoleId.employee ||
                    leaveUserDetails?.roleId === RoleId.intern) &&
                    managersList.map((manager: IUser) => (
                      <MenuItem key={manager?.id} value={manager?.id}>
                        <Typography>{`${manager.firstName} ${manager.lastName}`}</Typography>
                      </MenuItem>
                    ))}
                  {leaveUserDetails?.roleId === RoleId.manager && (
                    <MenuItem key={superAdmin?.id} value={superAdmin?.id}>
                      <Typography>{`${superAdmin.firstName} ${superAdmin.lastName}`}</Typography>
                    </MenuItem>
                  )}
                </RHFSelect>
                <RHFFilePickerField
                  name="attachment"
                  label={Strings.field.attachment}
                  accept=".pdf,.png,.jpg,.jpeg"
                />
                {fileError && (
                  <Typography color="error" variant="caption" sx={{ pl: 2 }}>
                    {fileError}
                  </Typography>
                )}
                <RHFTextField
                  name="reason"
                  label={Strings.field.reason}
                  required
                  fullWidth
                  variant="outlined"
                  multiline
                  minRows={5}
                />
              </Stack>
            </DialogContent>
          </Scrollbar>
          <Divider />
          <DialogActions>
            <Button variant="outlined" color="inherit" onClick={handleOnCloseLeavePopup}>
              {Strings.button.cancel}
            </Button>
            <LoadingButton
              variant="contained"
              color="primary"
              loading={addExistingLeaveLoading}
              onClick={methods.handleSubmit(onSubmitSend)}
            >
              {Strings.button.save}
            </LoadingButton>
          </DialogActions>
        </Dialog>
        {selectedDates.from ?? selectedDates.to ? (
          <SelectPeriod
            open={selectPeriodDialog.visible}
            onClose={selectPeriodDialog.hide}
            selectedDates={selectedDates}
            totalLeavesCalculated={handleTotalLeaves}
            handleSelectPeriod={handleSelectedPeriod}
            methods={methods as unknown as UseFormReturn<ILeaveSchema>}
          />
        ) : null}
      </FormProvider>
    </>
  );
};

export { AddExistingLeave };
